/**
 * Persisted column-visibility wiring for a grid's `options.columnVisibility`.
 *
 * Every grid that lets a user hide/show columns needs the same two things: a
 * user preference holding the visibility model, and a stable `{ model,
 * onChange }` object so passing it into EntityGrid/ServerDataGrid doesn't
 * recreate the grid's column state on every render. Centralizes that pairing
 * so each grid only supplies its preference-key suffix and default model.
 *
 * Contains:
 * - useColumnVisibilityModel — persisted model + memoized { model, onChange }
 */

import { useMemo } from 'react';

import useGridPreferenceKey from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useUserPreference from '@sophocles/hooks/useUserPreference';

const EMPTY_MODEL = {};

/**
 * @param {string} prefKeySuffix - fills `grid.<prefKeySuffix>.ct<customerTypeId>.columnVisibility.v2`
 * @param {object} [defaultModel] - initial visibility model before any preference is saved
 * @returns {{ model: object, onChange: (next: object) => void }}
 */
export default function useColumnVisibilityModel(prefKeySuffix, defaultModel = EMPTY_MODEL) {
  const prefKey = useGridPreferenceKey(prefKeySuffix, 'columnVisibility.v2');
  const [model, setModel] = useUserPreference(prefKey, defaultModel);
  return useMemo(() => ({ model, onChange: setModel }), [model, setModel]);
}
